<template>
  <div>
    <table class="table table-condensed table-lignes">
      <thead>
        <tr>
          <th class="colonne-description">Description</th>
          <th class="colonne-nombre">Qté</th>
          <th class="colonne-nombre">Prix</th>
          <th v-if="colonneTva" class="colonne-nombre">% TVA</th>
          <th class="colonne-nombre">Total</th>
          <th class="colonne-actions"></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(ligne, index) in lignes" :key="ligne.id">
          <td class="colonne-description">
            <input type="text"
                   class="form-control input-sm"
                   ref="description"
                   :list="idDatalist"
                   :value="ligne.description"
                   placeholder="Désignation de la prestation"
                   @input="surSaisieDescription(index, $event.target.value)"
                   @keydown="surTouche($event, index)">
          </td>
          <td class="colonne-nombre">
            <input type="text"
                   class="form-control input-sm text-right"
                   :value="ligne.quantite"
                   @input="modifier(index, 'quantite', $event.target.value)"
                   @keydown="surTouche($event, index)">
          </td>
          <td class="colonne-nombre">
            <input type="text"
                   class="form-control input-sm text-right"
                   :value="ligne.prixUnitaire"
                   @input="modifier(index, 'prixUnitaire', $event.target.value)"
                   @blur="modifier(index, 'prixUnitaire', formaterPrix($event.target.value))"
                   @keydown="surTouche($event, index)">
          </td>
          <td v-if="colonneTva" class="colonne-nombre">
            <input v-if="tvaParLigne"
                   type="text"
                   class="form-control input-sm text-right"
                   :value="ligne.tauxTva"
                   @input="modifier(index, 'tauxTva', $event.target.value)"
                   @keydown="surTouche($event, index)">
            <span v-else class="taux-global" title="Taux unique, modifiable dans le bloc TVA">
              {{ tauxGlobal }} %
            </span>
          </td>
          <td class="colonne-nombre montant-ligne">
            {{ fm(montants[index]) }}
          </td>
          <td class="colonne-actions">
            <button type="button" class="btn btn-default btn-xs"
                    title="Dupliquer la ligne (Ctrl+D)"
                    @click="$emit('dupliquer', index)">
              <i class="fa fa-copy"></i>
            </button>
            <button type="button" class="btn btn-default btn-xs"
                    title="Monter la ligne (Alt+flèche haut)"
                    :disabled="index === 0"
                    @click="$emit('deplacer', {index: index, delta: -1})">
              <i class="fa fa-arrow-up"></i>
            </button>
            <button type="button" class="btn btn-default btn-xs"
                    title="Descendre la ligne (Alt+flèche bas)"
                    :disabled="index === lignes.length - 1"
                    @click="$emit('deplacer', {index: index, delta: 1})">
              <i class="fa fa-arrow-down"></i>
            </button>
            <button type="button" class="btn btn-danger btn-xs"
                    title="Supprimer la ligne"
                    @click="$emit('supprimer', index)">
              <i class="fa fa-trash"></i>
            </button>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Descriptions déjà utilisées : la sélection pré-remplit le dernier prix pratiqué -->
    <datalist :id="idDatalist">
      <option v-for="memo in suggestions" :key="memo.description" :value="memo.description"></option>
    </datalist>

    <button type="button" class="btn btn-default btn-sm" @click="ajouter()">
      <i class="fa fa-plus"></i> Ajouter une ligne
    </button>
    <span class="help-block aide-clavier">
      Entrée sur la dernière ligne pour en créer une nouvelle · Ctrl+D pour dupliquer ·
      Alt+↑/↓ pour déplacer
    </span>
  </div>
</template>

<script type="text/babel">
  import {formaterMontant, normaliserMontant} from '../../../utils/facture/calculs'

  export default {
    name: 'FactureLignes',
    props: {
      lignes: {type: Array, required: true},
      montants: {type: Array, required: true},
      tvaParLigne: {type: Boolean, default: false},
      colonneTva: {type: Boolean, default: false},
      tauxGlobal: {type: [Number, String], default: 0},
      devise: {type: String, default: 'EUR'},
      suggestions: {type: Array, default: function () { return [] }}
    },
    data () {
      return {
        idDatalist: 'facture-descriptions-' + Math.random().toString(36).slice(2, 8)
      }
    },
    methods: {
      fm (valeur) {
        return formaterMontant(valeur, this.devise)
      },
      modifier (index, champ, valeur) {
        this.$emit('modifier', {index: index, champ: champ, valeur: valeur})
      },
      // Remise en forme à la sortie du champ : l'utilisateur tape « 12,5 » et
      // relit « 12,50 », comme sur la facture
      formaterPrix (valeur) {
        return normaliserMontant(valeur).toFixed(2).replace('.', ',')
      },
      /**
       * Une description reprise dans les suggestions ramène le dernier prix
       * pratiqué, mais seulement si l'utilisateur n'a pas déjà saisi un prix :
       * on ne réécrit jamais une valeur qu'il a tapée lui-même.
       */
      surSaisieDescription (index, valeur) {
        this.modifier(index, 'description', valeur)
        const memo = this.suggestions.find(function (candidat) {
          return candidat.description === valeur
        })
        if (memo && !normaliserMontant(this.lignes[index].prixUnitaire)) {
          this.modifier(index, 'prixUnitaire', memo.prixUnitaire)
        }
      },
      ajouter () {
        this.$emit('ajouter')
        // Le parent a déjà ajouté la ligne : le dernier index est length - 1
        this.focaliserDescription(this.lignes.length - 1)
      },
      ligneEstVide (ligne) {
        return !String(ligne.description || '').trim() &&
          !normaliserMontant(ligne.prixUnitaire)
      },
      /**
       * Un seul gestionnaire plutôt que les modificateurs de touche de Vue :
       * l'alias `.delete` de Vue capture aussi Backspace, ce qui supprimerait
       * une ligne pendant une correction de texte.
       */
      surTouche (evenement, index) {
        const touche = evenement.key

        if (touche === 'Enter') {
          evenement.preventDefault()
          if (index === this.lignes.length - 1) {
            this.ajouter()
          } else {
            this.focaliserDescription(index + 1)
          }
          return
        }

        if (evenement.ctrlKey && (touche === 'd' || touche === 'D')) {
          evenement.preventDefault()
          this.$emit('dupliquer', index)
          this.focaliserDescription(index + 1)
          return
        }

        if (evenement.altKey && (touche === 'ArrowUp' || touche === 'ArrowDown')) {
          evenement.preventDefault()
          const delta = touche === 'ArrowUp' ? -1 : 1
          const cible = index + delta
          if (cible < 0 || cible >= this.lignes.length) return
          this.$emit('deplacer', {index: index, delta: delta})
          this.focaliserDescription(cible)
          return
        }

        if (touche === 'Delete' && this.lignes.length > 1 && this.ligneEstVide(this.lignes[index])) {
          evenement.preventDefault()
          this.$emit('supprimer', index)
          this.focaliserDescription(Math.max(index - 1, 0))
        }
      },
      /**
       * Vue ne réordonne pas `$refs` après une insertion au milieu d'un v-for :
       * on relit les champs dans l'ordre du DOM, seul ordre fiable.
       */
      focaliserDescription (index) {
        const composant = this
        this.$nextTick(function () {
          const champs = composant.$el.querySelectorAll('.table-lignes tbody tr td:first-child input')
          if (champs[index]) {
            champs[index].focus()
            champs[index].select()
          }
        })
      }
    }
  }
</script>

<style scoped>
  .table-lignes {
    margin-bottom: 10px;
  }

  .table-lignes > thead > tr > th {
    border-bottom-width: 1px;
    font-size: 12px;
    font-weight: 700;
  }

  .table-lignes > tbody > tr > td {
    vertical-align: middle;
    padding: 4px;
  }

  .colonne-nombre {
    text-align: right;
    width: 110px;
  }

  .colonne-actions {
    width: 128px;
    white-space: nowrap;
    text-align: right;
  }

  .taux-global {
    display: inline-block;
    padding-right: 12px;
    color: #777;
  }

  .montant-ligne {
    white-space: nowrap;
    font-weight: 700;
  }

  .aide-clavier {
    display: inline-block;
    margin-left: 12px;
    margin-bottom: 0;
    font-size: 11px;
  }
</style>



// WEBPACK FOOTER //
// src/components/Outils/Facture/FactureLignes.vue